<script lang="ts">
  import { getFlowContext } from "conversion_flows/lib/helpers"
  import {
    FlowEventAction,
    type GenericFlowEvent,
  } from "conversion_flows/types/flowEvent"
  import type { WebsiteScriptData } from "conversion_flows/types/websiteScriptData"
  /**
   * Container for modules (iframes)
   * Provides an interaction layer with iframes - keeps the iframe size corresponding to content size
   *
   * TODO:
   * Mostly stuff from widgets/trade_in_calculator_embed.js.coffee
   * - [ ] maxHeight - add a maximum height beyond which the iframe becomes scrollable inside (useful for modal dialogues)
   * - [ ] expose API so that `AuEmbed.TradeInCalculator.renderForm` can be called from anywhere on the page
   */

  import { nanoid } from "nanoid"
  import { createEventDispatcher, onMount, tick } from "svelte"
  import type ModuleTypes from "website_script/constants/ModuleTypes"

  const dispatch = createEventDispatcher()

  /** the source of the iframe */
  export let url: string
  /** whether the iframe adjusts its height automatically */
  export let autoheight: boolean = true
  export let appendParamsKey: string = "&"
  /** data for appending to flow events */
  export let websiteScriptData: WebsiteScriptData
  export let moduleType: ModuleTypes
  export let carId: string | null = null

  // Initial iframe height
  let height: number = 0

  // Identify what ID the parent knows this child as
  // Will be unique for each instance of this component,
  // so that messages are parsed appropriately
  export let id: `au-trade-in-calculator-${string}` = `au-trade-in-calculator-${nanoid(
    5
  )}`

  // pym.js message regex
  // Needed to support pym.js messages from inside iframe
  const MESSAGE_DELIMITER: string = "xPYMx"
  const messageRegex: RegExp = new RegExp(
    "^pym" +
      MESSAGE_DELIMITER +
      id +
      MESSAGE_DELIMITER +
      "(\\S+)" +
      MESSAGE_DELIMITER +
      "(.*)$"
  )

  let iframeSrc: string
  // Append the id to the iframe src so messages are targeted appropriately
  $: iframeSrc = `${url}${appendParamsKey}parent_id=${encodeURIComponent(id)}`

  const handleJsonMessage = (data: {
    intendedReceiver?: string
    action?: string
    customerDetails: {
      email?: string
      name?: string
      phone_number?: string
      comments?: string
      self_valuation?: string
      requiresVerification?: boolean
      checkLeadVerificationUrl?: string
    }
    valuated?: boolean
    buyerCarEstimatedMarketPrice?: number | null
    buyerCarValuation?: number | null
    buyerCarValuationInterval?: {
      upperLimit: number
      lowerLimit: number
    } | null
    buyerCar?: unknown
    autouncleRatingIntervals?: {
      rating: string
      upperLimit: number
      lowerLimit: number
    }
  }) => {
    // The message is not intended to be handled by this container
    if (data.intendedReceiver !== id) return

    // Capture misc event data into an object
    const eventData = { ...data }
    delete eventData.intendedReceiver
    delete eventData.action

    const customerDetails = eventData.customerDetails

    const flowContext = getFlowContext({
      websiteScriptData,
      moduleType,
      carId,
      departmentId: websiteScriptData.depId ?? null,
      checkLeadVerificationUrl: customerDetails?.checkLeadVerificationUrl,
    })

    delete customerDetails?.checkLeadVerificationUrl

    // Dispatch equivalent lead flow submitted/completed events for these events
    if (data.action === "TradeInCalculator.customerDetailsSent") {
      dispatch("flowEvent", {
        action: "leadFlowSubmitted",
        data: {
          ...eventData,
          flowContext,
        },
      })
      if (eventData.customerDetails.requiresVerification) {
        dispatch("flowEvent", {
          action: "checkForLeadVerification",
          data: {
            ...eventData,
            flowContext,
          },
        })
      } else {
        dispatch("flowEvent", {
          action: "leadFlowCompleted",
          data: {
            ...eventData,
            flowContext,
          },
        })
      }
    }

    // Handles events from the iframe
    if (data.action === "TradeInCalculator.viewChanged") {
      dispatch("viewChanged")
    } else if (data.action === "TradeInCalculator.carDetailsStarted") {
      dispatch("flowEvent", {
        action: "carDetailsStarted",
        data: eventData,
      })
    } else if (data.action === "TradeInCalculator.carDetailsSent") {
      dispatch("flowEvent", {
        action: "carDetailsSent",
        data: eventData,
      })
      dispatch("flowEvent", {
        action: "valuation",
        data: {
          car: data.buyerCar,
          autouncleRatingIntervals: data.autouncleRatingIntervals,
          valuated: data.valuated,
          estimatedMarketPrice: data.buyerCarEstimatedMarketPrice,
          carValuation: data.buyerCarValuation,
          carValuationInterval: data.buyerCarValuationInterval,
        },
      })
    } else if (data.action === "TradeInCalculator.customerDetailsSent") {
      dispatch("flowEvent", {
        action: "customerDetailsSent",
        data: eventData,
      })
    }
  }

  /**
   * Handles postMessage(s) with information from iframe
   * Made compatible with pym.js messages
   */
  const handleMessage = (e: MessageEvent) => {
    // Handle height messages
    if (e.data.height) {
      height = parseInt(e.data.height)
    }

    // Only interested in string messages for height
    if (typeof e.data !== "string") return

    let match = e.data.match(messageRegex)

    // If there's no match or too many matches in the message, punt.
    if (!match || match.length !== 3) {
      try {
        let data = JSON.parse(e.data)
        handleJsonMessage(data)
      } catch (error) {
        return
      }
      return
    }

    // We are not interested in height messages
    if (!autoheight) return

    const messageType = match[1]
    const message = match[2]

    if (messageType === "height" && message) {
      height = parseInt(message)
    }
  }

  onMount(async () => {
    // We fire the event asynchronously, as it is easier to test
    await tick()
    const eventDetails: GenericFlowEvent = {
      action: FlowEventAction.leadFlowStarted,
      data: {
        customerDetails: null,
        flowContext: getFlowContext({
          websiteScriptData,
          moduleType,
          carId,
          departmentId: websiteScriptData.depId ?? null,
        }),
      },
    }
    dispatch("flowEvent", eventDetails)
  })
</script>

<svelte:window on:message={handleMessage} />
<iframe
  src={iframeSrc}
  title="iframe"
  height={`${height}px`}
  style={`height: ${height}px;`}
  on:load
/>

<style>
  iframe {
    border: 0px none;
    margin: 0;
    padding: 0;
    width: 100%;
  }
</style>
